import type { PersistentIndexedDB } from "./persistentIndexedDB";

/**
 * Runtime controls over the iPad Lockdown-Mode persistence stand-in, split
 * from lockdownStandInStorage.ts so consumers that only need to CONTROL an
 * already-installed stand-in (SignOut) don't statically pull the heavy
 * install path — fake-indexeddb alone is ~140KB of entry-bundle parse, paid
 * by every platform for a module only Lockdown-Mode iPads execute. The
 * installer (mobile entry) registers its handle here; everything below is a
 * no-op wherever nothing was installed.
 */

let installed: PersistentIndexedDB | null = null;

/** Called by installLockdownStandInStorage once its stand-in is live. */
export function registerLockdownPersistence(handle: PersistentIndexedDB): void {
  installed = handle;
}

/** Whether this session keeps its durable copy in native files. */
export function isLockdownPersistenceActive(): boolean {
  return installed != null;
}

/**
 * Stop persisting, permanently, and drain writes already in flight. Called on
 * sign-out before the databases are deleted, so a deferred write cannot land
 * after the delete and leave the signed-out account's round on disk.
 *
 * The deletes themselves need no help: `Dexie.delete()` reaches
 * `deleteDatabase()` on the wrapper, which removes the native files.
 *
 * A no-op on every platform that never installed the stand-in.
 */
export async function suspendLockdownPersistence(): Promise<void> {
  if (!installed) return;
  installed.suspend();
  await installed.flush();
}

/**
 * Called when a sign-out purge fails and the user stays in the app without
 * reloading. Suspension alone would let their next offline edit complete in
 * memory and then vanish on force-quit; this makes such an edit FAIL instead,
 * so the loss can never be silent. A no-op off the stand-in.
 */
export function abandonLockdownPersistence(): void {
  installed?.abandon();
}

/**
 * Await the durable files catching up to memory — deferred writes in flight
 * AND anything a transient failure left dirty. The barrier a privacy-motivated
 * CLEAR needs on the stand-in: a deferred store's delete commits in memory
 * before its file rewrite lands, so without this a force-quit in that window
 * resurrects the cleared rows on the next launch. (On every other platform a
 * committed IndexedDB delete is already durable, and this is a no-op.)
 * `rethrow` so the caller knows durability was NOT reached — invariant 8's
 * dirty-retry still covers the failure if they proceed anyway.
 */
export async function flushLockdownPersistence(): Promise<void> {
  await installed?.flush({ rethrow: true });
}
